<script setup>
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useShortLinkShare } from '~/composables/useShortLinkShare';
import { useJoinSettings } from '~/composables/useJoinSettings';

const props = defineProps({
  targetPath: {
    type: String,
    default: null
  },
  disabled: {
    type: Boolean,
    default: false
  },
  // Admins can share two different things: the page anyone can read, and the
  // link that lets someone join. Pass the entity to be asked which; without it
  // this stays a one-tap share of the current page.
  joinReferenceType: {
    type: Number,
    default: null
  },
  joinReferenceId: {
    type: String,
    default: null
  },
  canShareJoin: {
    type: Boolean,
    default: false
  }
});

const route = useRoute();
const { setAlertMessage } = useAlertService();

const {
  shortLink,
  isLoading,
  error,
  hasError,
  createShortLink,
  copyToClipboard,
  getSocialShareUrls,
  openShareWindow
} = useShortLinkShare();

const successDialog = ref(false);
const errorDialog = ref(false);
const copied = ref(false);

// The chooser, and the path the chosen option resolved to.
const chooseDialog = ref(false);
const overridePath = ref(null);
const offersJoin = computed(() =>
    props.canShareJoin && props.joinReferenceId != null && props.joinReferenceType != null);

const {settings: joinSettings, pending: joinPending, errors: joinErrors, load: loadJoinSettings} =
    useJoinSettings(() => props.joinReferenceType, () => props.joinReferenceId);
// A failed load is not the same thing as "joining is off", and must not be
// reported as one.
const joinLoadFailed = computed(() => !joinPending.value && !joinSettings.value && !!joinErrors.value);
const joinable = computed(() => joinSettings.value?.enabled !== false && !!joinSettings.value?.link?.slug);
const entityTypeName = computed(() => joinSettings.value?.entityTypeName ?? 'page');

const pathToShare = computed(() => overridePath.value || props.targetPath || route.path);

const socialUrls = computed(() => {
  if (!shortLink.value) return null;
  return getSocialShareUrls(shortLink.value.shortUrl, 'Check this out on Tap Leagues!');
});

const share = async (path) => {
  copied.value = false;
  overridePath.value = path;
  const result = await createShortLink(pathToShare.value);
  if (result) {
    successDialog.value = true;
  } else {
    errorDialog.value = true;
  }
};

const handleClick = async () => {
  if (props.disabled) return;

  if (offersJoin.value) {
    chooseDialog.value = true;
    // Fresh every time. The Joining card on the same page can make a new link
    // or switch joining off, and a cached copy would then share a dead link.
    joinSettings.value = null;
    await loadJoinSettings();
    return;
  }
  await share(null);
};

const sharePage = async () => {
  chooseDialog.value = false;
  await share(null);
};

const shareJoinLink = async () => {
  if (!joinable.value) return;
  chooseDialog.value = false;
  await share(`/invite/${joinSettings.value.link.slug}`);
};

const handleCopy = async () => {
  if (!shortLink.value) return;

  const success = await copyToClipboard(shortLink.value.shortUrl);

  if (success) {
    copied.value = true;
  } else {
    setAlertMessage({
      message: 'Failed to copy link',
      color: 'error',
      location: 'bottom'
    });
  }
};

const handleSocialShare = (platform) => {
  if (!socialUrls.value) return;

  const url = socialUrls.value[platform];
  if (url) {
    openShareWindow(url);
  }
};

const closeSuccessDialog = () => {
  successDialog.value = false;
  copied.value = false;
};

const closeErrorDialog = () => {
  errorDialog.value = false;
};
</script>

<template>
  <slot v-bind:click="handleClick" v-bind:loading="isLoading"></slot>

  <!-- Which link? Admins have two to give out. -->
  <tl-dialog v-model="chooseDialog" title="Share" :max-width="420">
    <v-skeleton-loader v-if="joinPending && !joinSettings" type="list-item-two-line@2"/>
    <template v-else>
      <button type="button" class="slsw-choice" @click="sharePage">
        <v-icon size="20" color="primary" icon="mdi-eye-outline"/>
        <div>
          <div class="slsw-choice-t">Share the page</div>
          <div class="slsw-choice-s">Anyone with the link can see this {{ entityTypeName }}.</div>
        </div>
      </button>

      <button v-if="joinable" type="button" class="slsw-choice" @click="shareJoinLink">
        <v-icon size="20" color="primary" icon="mdi-account-multiple-plus"/>
        <div>
          <div class="slsw-choice-t">Share the join link</div>
          <div class="slsw-choice-s">Whoever opens it can join this {{ entityTypeName }}.</div>
        </div>
      </button>
      <div v-else-if="joinLoadFailed" class="slsw-note">
        <v-icon size="16" class="mr-2" icon="mdi-alert-circle-outline"/>
        <span>
          The join link could not be loaded.
          <a href="#" @click.prevent="loadJoinSettings">Try again</a>
        </span>
      </div>
      <div v-else class="slsw-note">
        <v-icon size="16" class="mr-2" icon="mdi-cancel"/>
        <span>
          Joining is switched off, so there is no join link to share. Turn it back on in Join settings.
        </span>
      </div>
    </template>
  </tl-dialog>

  <!-- Success Dialog -->
  <v-dialog v-model="successDialog" max-width="390">
    <v-card class="bg-surface d-flex flex-column pa-0" :elevation="0" style="border:1px solid rgb(var(--v-theme-borderLight)); border-radius:14px;">
      <div class="d-flex align-center justify-space-between pa-4" style="border-bottom: 1px solid rgb(var(--v-theme-borderLight));">
        <span class="text-subtitle-1 font-weight-bold">Share link</span>
        <v-btn icon="mdi-close" variant="text" size="small" @click="closeSuccessDialog"/>
      </div>

      <v-card-text class="pa-5 text-text" style="font-size: 1.25rem;">
        <div class="mb-4">
          <div class="text-caption text-medium-emphasis mb-2">Copy this link to share it</div>
          <div class="d-flex align-center">
            <v-text-field
                :model-value="shortLink?.shortUrl"
                readonly
                variant="outlined"
                density="compact"
                hide-details
                class="mr-2"
            ></v-text-field>
            <v-btn
                icon="mdi-content-copy"
                color="primary"
                variant="flat"
                @click="handleCopy"
            ></v-btn>
          </div>
          <div v-if="copied" class="text-caption text-success mt-1">
            <v-icon size="small" class="mr-1">mdi-check</v-icon>
            Copied!
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="pa-0 pa-4" style="border-top: 1px solid rgb(var(--v-theme-borderLight));">
        <v-spacer/>
        <v-btn
            size="large"
            class="px-5"
            color="primary"
            variant="flat"
            @click="closeSuccessDialog"
        >
          <v-icon color="white" size="16" class="mr-2" icon="mdi-check"></v-icon>
          Done
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Error Dialog -->
  <v-dialog v-model="errorDialog" max-width="390">
    <v-card class="bg-surface d-flex flex-column pa-0" :elevation="0" style="border:1px solid rgb(var(--v-theme-borderLight)); border-radius:14px;">
      <div class="d-flex align-center justify-space-between pa-4" style="border-bottom: 1px solid rgb(var(--v-theme-borderLight));">
        <span class="text-subtitle-1 font-weight-bold">Failed to Create Share Link</span>
        <v-btn icon="mdi-close" variant="text" size="small" @click="closeErrorDialog"/>
      </div>

      <v-card-text class="pa-5 text-text" style="font-size: 1.25rem;">
        <div v-if="hasError && error" class="text-body-2 text-medium-emphasis">
          <div v-for="(messages, field) in error" :key="field">
            <div v-for="(message, index) in messages" :key="index">
              {{ message }}
            </div>
          </div>
        </div>
        <div v-else class="text-body-2 text-medium-emphasis">
          An unexpected error occurred. Please try again.
        </div>
      </v-card-text>

      <v-card-actions class="pa-0 pa-4" style="border-top: 1px solid rgb(var(--v-theme-borderLight));">
        <v-spacer></v-spacer>
        <v-btn
            size="large"
            class="px-5"
            color="error"
            variant="flat"
            @click="closeErrorDialog"
        >
          <v-icon color="white" size="16" class="mr-2" icon="mdi-close"></v-icon>
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">


.slsw-choice {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 12px 14px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;

  & + & {
    margin-top: 10px;
  }

  &:hover {
    background: rgb(var(--v-theme-light));
  }
}

.slsw-choice-t {
  font-size: 13.5px;
  font-weight: 700;
}

.slsw-choice-s {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
  line-height: 1.4;
}

.slsw-note {
  display: flex;
  align-items: flex-start;
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgb(var(--v-theme-light));
  font-size: 12.5px;
  color: rgb(var(--v-theme-charcoal));
  line-height: 1.45;
}
</style>
